import { getBatteryServiceLife } from '../../helpers/getBatteryServiceLife';
import { getUsedWithNames } from '../../helpers/getUsedWithNames';

import { HorizontalContainer, VerticalContainer, Wrapper } from '@american-autonomy/ag-ui';

import RingGauge from 'pages/MaintenancePage/components/RingGauge';
import useTranslation from 'pages/MaintenancePage/hooks/useTranslation';

import BatteryStatusPill from './components/BatteryStatusPill';
import { TEST_ID } from './constants';
import type { FunctionComponent, Props } from './types';

import styles from './styles.module.scss';

const BatteryCard: FunctionComponent<Props> = ({
    battery,
    className = '',
    cycleInterval,
    droneNames,
    onOpen,
    testId = TEST_ID
}) => {
    const t = useTranslation();
    const serviceLife = getBatteryServiceLife({ battery, cycleInterval });
    const statusLabels = { aging: t.BATTERY_STATUS_AGING, ok: t.BATTERY_STATUS_OK };
    const intervalText = cycleInterval ? `${cycleInterval.value}` : t.EM_DASH;
    const cycleText = `${battery.cycleCount} / ${intervalText} ${t.CYCLES_UNIT}`;
    const usedWithNames = getUsedWithNames({ droneIds: battery.droneIds, droneNames });
    const usedWithText = usedWithNames.length > 0 ? usedWithNames.join(', ') : t.EM_DASH;
    const lastSyncStaleClass = battery.lastSync.stale ? styles.isStale : '';

    return (
        <VerticalContainer
            className={`${styles.batteryCard} ${className}`}
            onClick={() => onOpen(battery.id)}
            testId={testId}>
            <HorizontalContainer className={styles.batteryHead}>
                <Wrapper className={styles.batterySerial}>{battery.serialNumber}</Wrapper>

                <BatteryStatusPill label={statusLabels[battery.status]} status={battery.status} />
            </HorizontalContainer>

            <Wrapper className={`${styles.lastSync} ${lastSyncStaleClass}`}>
                {`${t.LOG_SYNCED} ${battery.lastSync.label}`}
            </Wrapper>

            <Wrapper className={styles.ringRow}>
                <RingGauge
                    label={t.CYCLE_LIFE_LABEL}
                    notConfiguredText={t.NOT_CONFIGURED}
                    serviceLife={serviceLife}
                    size='large'
                />
            </Wrapper>

            <VerticalContainer className={styles.specs}>
                <HorizontalContainer className={styles.specStrip}>
                    <Wrapper className={styles.specLabel}>{t.CYCLES_LABEL}</Wrapper>

                    <Wrapper className={styles.specValue}>{cycleText}</Wrapper>
                </HorizontalContainer>

                <HorizontalContainer className={styles.specStrip}>
                    <Wrapper className={styles.specLabel}>{t.USED_WITH}</Wrapper>

                    <Wrapper className={styles.specValue}>{usedWithText}</Wrapper>
                </HorizontalContainer>
            </VerticalContainer>
        </VerticalContainer>
    );
};

export default BatteryCard;
